// File: frontend/src/pages/RegisteredFighters.jsx

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";

import { useParams, Link } from "react-router-dom";
import API from "../api/api";
import * as XLSX from "xlsx";
import { AuthContext } from "../context/AuthContext";

export default function RegisteredFighters() {
  const { id: tournamentId } = useParams();

  const { user } = useContext(AuthContext);

  const isAdmin = user?.role === "admin";

  const [tournament, setTournament] = useState(null);

  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [category, setCategory] = useState("main");
  const [gender, setGender] = useState("male");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedWeight, setSelectedWeight] =
    useState("");

  const UPLOADS_BASE =
    "https://warfc.com/api/uploads";

  // ==========================================================
  // IMAGE
  // ==========================================================

  const resolveImage = (
    imagePath,
    type = "fighter"
  ) => {
    if (!imagePath) return "";

    if (
      String(imagePath).startsWith("http")
    ) {
      return imagePath;
    }

    let clean = String(imagePath).replace(
      /^\/+/,
      ""
    );

    if (clean.startsWith("uploads/")) {
      clean = clean.replace(
        /^uploads\//,
        ""
      );
    }

    if (
      type === "club" &&
      !clean.startsWith("clubs/")
    ) {
      clean = `clubs/${clean}`;
    }

    return `${UPLOADS_BASE}/${clean}`;
  };

  // ==========================================================
  // DATE FORMAT
  // ==========================================================

  const formatTournamentDate = (
    startDate,
    endDate
  ) => {
    if (!startDate) return "";

    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];

    const getParts = (value) => {
      const dateOnly = String(value).split("T")[0];

      const parts = dateOnly.split("-");

      if (parts.length !== 3) {
        return null;
      }

      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);

      if (
        !year ||
        !month ||
        !day
      ) {
        return null;
      }

      return {
        year,
        month,
        day,
      };
    };

    const start = getParts(startDate);

    if (!start) return "";

    // No end date
    if (!endDate) {
      return `${start.day} ${
        months[start.month - 1]
      } ${start.year}`;
    }

    const end = getParts(endDate);

    if (!end) {
      return `${start.day} ${
        months[start.month - 1]
      } ${start.year}`;
    }

    // Same day
    if (
      start.year === end.year &&
      start.month === end.month &&
      start.day === end.day
    ) {
      return `${start.day} ${
        months[start.month - 1]
      } ${start.year}`;
    }

    // Same month + same year
    if (
      start.year === end.year &&
      start.month === end.month
    ) {
      return `${start.day}–${end.day} ${
        months[start.month - 1]
      } ${start.year}`;
    }

    // Same year, different month
    if (
      start.year === end.year
    ) {
      return `${start.day} ${
        months[start.month - 1]
      } – ${end.day} ${
        months[end.month - 1]
      } ${start.year}`;
    }

    // Different years
    return `${start.day} ${
      months[start.month - 1]
    } ${start.year} – ${end.day} ${
      months[end.month - 1]
    } ${end.year}`;
  };

  // ==========================================================
  // NORMALIZE
  // ==========================================================

  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

 // ==========================================================
// LOAD TOURNAMENT
// ==========================================================

useEffect(() => {
  if (!tournamentId) return;

  let cancelled = false;

  async function loadTournament() {
    try {
      /*
       * First try the normal tournament detail endpoint.
       */
      let tournamentData = null;

      try {
        const res = await API.get(
          `/tournaments/${tournamentId}`
        );

        tournamentData =
          res?.tournament || res || null;
      } catch (detailError) {
        console.warn(
          "Tournament detail endpoint failed:",
          detailError
        );
      }

      /*
       * If the detail endpoint does not contain
       * the complete date information, use the same
       * public list endpoint that TournamentList uses.
       *
       * This is important because the tournament listing
       * already correctly knows the 19–20 September range.
       */
      const startExists =
        tournamentData?.startdate ||
        tournamentData?.start_date ||
        tournamentData?.startDate;

      const endExists =
        tournamentData?.enddate ||
        tournamentData?.end_date ||
        tournamentData?.endDate;

      if (!startExists || !endExists) {
        try {
          const listRes = await API.get(
            "/tournaments/public/list"
          );

          const listData =
            listRes?.data ?? listRes;

          if (Array.isArray(listData)) {
            const found = listData.find((t) => {
              const id =
                t.id ??
                t.tournamentid ??
                t.tournament_id;

              return (
                Number(id) === Number(tournamentId)
              );
            });

            if (found) {
              /*
               * Merge the detail endpoint data with
               * the public list data.
               *
               * Detail data stays primary.
               * Missing fields are filled from the list.
               */
              tournamentData = {
                ...(found || {}),
                ...(tournamentData || {}),
              };

              /*
               * Explicitly preserve date fields if the
               * detail endpoint uses different names.
               */
              if (
                !tournamentData.startdate &&
                found.startdate
              ) {
                tournamentData.startdate =
                  found.startdate;
              }

              if (
                !tournamentData.enddate &&
                found.enddate
              ) {
                tournamentData.enddate =
                  found.enddate;
              }

              if (
                !tournamentData.start_date &&
                found.start_date
              ) {
                tournamentData.start_date =
                  found.start_date;
              }

              if (
                !tournamentData.end_date &&
                found.end_date
              ) {
                tournamentData.end_date =
                  found.end_date;
              }
            }
          }
        } catch (listError) {
          console.warn(
            "Could not load tournament date from public list:",
            listError
          );
        }
      }

      if (cancelled) return;

      setTournament(
        tournamentData || null
      );
    } catch (err) {
      console.error(
        "Failed to load tournament:",
        err
      );

      /*
       * Do NOT break the whole page.
       * Registration data loads independently.
       */
    }
  }

  loadTournament();

  return () => {
    cancelled = true;
  };
}, [tournamentId]);

  // ==========================================================
  // LOAD REGISTRATIONS
  // ==========================================================

  useEffect(() => {
    if (!tournamentId) return;

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const res = await API.get(
          `/tournaments/${tournamentId}/registration`
        );

        const data = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(
              res?.registrations
            )
          ? res.registrations
          : Array.isArray(res)
          ? res
          : [];

        const mapped = data
          .map((r) => {
            const rawGender =
              r.registration_gender ??
              r.gender ??
              r.fighter_gender ??
              "";

            const rawType =
              r.type ??
              r.registration_type ??
              "main";

            return {
              registration_id:
                r.registration_id ??
                r.id ??
                null,

              fighter_id:
                r.fighter_id ??
                null,

              name:
                `${r.name || ""} ${
                  r.surname || ""
                }`.trim() ||
                "Unknown Fighter",

              image: resolveImage(
                r.fighter_image ??
                  r.image_url ??
                  r.image
              ),

              club:
                r.club_name ||
                r.club ||
                "-",

              club_id:
                r.club_id ??
                r.clubId ??
                null,

              club_logo: resolveImage(
                r.club_logo ??
                  r.club_logo_url,
                "club"
              ),

              gender: normalize(
                rawGender
              ),

              type: normalize(rawType),

              paid: Boolean(r.paid),

              approved: Boolean(
                r.approved
              ),

              weighed_in: Boolean(
                r.weighed_in
              ),

              age_division:
                r.age_division ||
                r.age_division_name ||
                r.division_name ||
                "-",

              division_id:
                r.division_id ??
                null,

              division_weightclass_id:
                r.division_weightclass_id ??
                null,

              weightclass_name:
                r.weightclass_name ||
                r.weight_class ||
                "Unknown",

              weight:
                r.weight != null &&
                r.weight !== ""
                  ? Number(r.weight)
                  : null,

              actual_weight:
                r.actual_weight !== null &&
                r.actual_weight !== undefined &&
                r.actual_weight !== ""
                  ? Number(r.actual_weight)
                  : null,
            };
          })
          .filter(
            (fighter) =>
              fighter.fighter_id
          );

        if (!cancelled) {
          setFighters(mapped);
        }
      } catch (err) {
        console.error(
          "Failed to load tournament fighters:",
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load tournament fighters"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  // ==========================================================
  // SUMMARY
  // ==========================================================

  const maleCount = useMemo(
    () =>
      fighters.filter(
        (f) => f.gender === "male"
      ).length,
    [fighters]
  );

  const femaleCount = useMemo(
    () =>
      fighters.filter(
        (f) => f.gender === "female"
      ).length,
    [fighters]
  );

  const clubCount = useMemo(() => {
    const clubIds = new Set();
    const clubNames = new Set();

    fighters.forEach((fighter) => {
      if (fighter.club_id) {
        clubIds.add(
          String(fighter.club_id)
        );
      } else if (
        fighter.club &&
        fighter.club !== "-"
      ) {
        clubNames.add(
          normalize(fighter.club)
        );
      }
    });

    return (
      clubIds.size +
      clubNames.size
    );
  }, [fighters]);

  // ==========================================================
  // CATEGORY
  // ==========================================================

  const categoryFighters = useMemo(() => {
    return fighters.filter(
      (fighter) => {
        if (
          category ===
          "altan_garaa"
        ) {
          return (
            fighter.type ===
            "altan_garaa"
          );
        }

        return (
          fighter.type !==
          "altan_garaa"
        );
      }
    );
  }, [fighters, category]);

  // ==========================================================
  // GENDER
  // ==========================================================

  const genderFighters = useMemo(() => {
    return categoryFighters.filter(
      (fighter) =>
        fighter.gender === gender
    );
  }, [
    categoryFighters,
    gender,
  ]);

  // ==========================================================
  // AVAILABLE AGE DIVISIONS
  // ==========================================================

  const availableAges = useMemo(() => {
    const map = new Map();

    genderFighters.forEach(
      (fighter) => {
        const name =
          fighter.age_division;

        if (
          !name ||
          name === "-"
        ) {
          return;
        }

        const key =
          normalize(name);

        if (!map.has(key)) {
          map.set(key, name);
        }
      }
    );

    return Array.from(
      map.values()
    ).sort((a, b) => {
      const aMatch =
        String(a).match(/\d+/);

      const bMatch =
        String(b).match(/\d+/);

      const aNum = aMatch
        ? Number(aMatch[0])
        : 999;

      const bNum = bMatch
        ? Number(bMatch[0])
        : 999;

      return aNum - bNum;
    });
  }, [genderFighters]);

  // ==========================================================
  // AUTO SELECT AGE
  // ==========================================================

  useEffect(() => {
    if (!availableAges.length) {
      setSelectedAge("");
      setSelectedWeight("");
      return;
    }

    const exists =
      availableAges.some(
        (age) =>
          normalize(age) ===
          normalize(selectedAge)
      );

    if (!exists) {
      setSelectedAge(
        availableAges[0]
      );

      setSelectedWeight("");
    }
  }, [
    availableAges,
    selectedAge,
  ]);

  // ==========================================================
  // AGE FILTER
  // ==========================================================

  const ageFighters = useMemo(() => {
    if (!selectedAge) return [];

    return genderFighters.filter(
      (fighter) =>
        normalize(
          fighter.age_division
        ) ===
        normalize(selectedAge)
    );
  }, [
    genderFighters,
    selectedAge,
  ]);

  // ==========================================================
  // AVAILABLE WEIGHTS
  // ==========================================================

  const availableWeights = useMemo(() => {
    const map = new Map();

    ageFighters.forEach(
      (fighter) => {
        const name =
          fighter.weightclass_name;

        if (
          !name ||
          name === "Unknown"
        ) {
          return;
        }

        const key =
          normalize(name);

        if (!map.has(key)) {
          map.set(key, {
            name,
            weight:
              fighter.weight,
          });
        }
      }
    );

    return Array.from(map.values()).sort((a, b) => {
  const aw =
    a.weight == null
      ? 9999
      : Number(a.weight);

  const bw =
    b.weight == null
      ? 9999
      : Number(b.weight);

  // Sort first by numerical weight
  if (aw !== bw) {
    return aw - bw;
  }

  // When weights are equal, regular/limited division comes first.
  // Example: 70kg → +70kg, -35kg → +35kg
  const aIsOpen = String(a.name || "")
    .trim()
    .startsWith("+");

  const bIsOpen = String(b.name || "")
    .trim()
    .startsWith("+");

  if (aIsOpen !== bIsOpen) {
    return aIsOpen ? 1 : -1;
  }

  return String(a.name || "").localeCompare(
    String(b.name || ""),
    undefined,
    { numeric: true }
  );
});
  }, [ageFighters]);

  // ==========================================================
  // AUTO SELECT WEIGHT
  // ==========================================================

  useEffect(() => {
    if (!availableWeights.length) {
      setSelectedWeight("");
      return;
    }

    const exists =
      availableWeights.some(
        (weight) =>
          normalize(weight.name) ===
          normalize(
            selectedWeight
          )
      );

    if (!exists) {
      setSelectedWeight(
        availableWeights[0].name
      );
    }
  }, [
    availableWeights,
    selectedWeight,
  ]);

  // ==========================================================
  // VISIBLE FIGHTERS
  // ==========================================================

  const visibleFighters = useMemo(() => {
    if (!selectedWeight) return [];

    return ageFighters.filter(
      (fighter) =>
        normalize(
          fighter.weightclass_name
        ) ===
        normalize(selectedWeight)
    );
  }, [
    ageFighters,
    selectedWeight,
  ]);

  // ==========================================================
// CLUB SUMMARY
// ==========================================================

const clubsSummary = useMemo(() => {
  const clubs = {};

  fighters.forEach((fighter) => {
    const clubName =
      fighter.club_name ||
      fighter.club ||
      "NO CLUB";

    const clubLogo =
      fighter.club_logo ||
      fighter.logo ||
      null;

    if (!clubs[clubName]) {
      clubs[clubName] = {
        name: clubName,
        logo: clubLogo,
        count: 0,
      };
    }

    clubs[clubName].count += 1;
  });

  return Object.values(clubs).sort(
    (a, b) => b.count - a.count
  );
}, [fighters]);

  // ==========================================================
  // CATEGORY COUNTS
  // ==========================================================

  const mainCount = useMemo(
    () =>
      fighters.filter(
        (f) =>
          f.type !==
          "altan_garaa"
      ).length,
    [fighters]
  );

  const altanCount = useMemo(
    () =>
      fighters.filter(
        (f) =>
          f.type ===
          "altan_garaa"
      ).length,
    [fighters]
  );

  // ==========================================================
  // CATEGORY CHANGE
  // ==========================================================

  const changeCategory = (
    value
  ) => {
    setCategory(value);
    setSelectedAge("");
    setSelectedWeight("");
  };

  // ==========================================================
  // GENDER CHANGE
  // ==========================================================

  const changeGender = (
    value
  ) => {
    setGender(value);
    setSelectedAge("");
    setSelectedWeight("");
  };

  // ==========================================================
  // EXPORT EXCEL
  // ADMIN ONLY
  // ==========================================================

  const exportToExcel = () => {
    if (!fighters.length) {
      return;
    }

    if (!isAdmin) {
      return;
    }

    const rows = fighters.map(
      (fighter, index) => ({
        "#": index + 1,

        Name: fighter.name,

        Gender:
          fighter.gender,

        "Age Division":
          fighter.age_division,

        "Weight Class":
          fighter.weightclass_name,

        Weight:
          fighter.weight != null
            ? `${fighter.weight}kg`
            : "-",

        "Actual Weight":
          fighter.actual_weight != null &&
          Number.isFinite(Number(fighter.actual_weight))
            ? `${Number(fighter.actual_weight).toFixed(1)}kg`
            : "-",

        Category:
          fighter.type ===
          "altan_garaa"
            ? "Altan Garaa"
            : "Undsen",

        Club:
          fighter.club,

        Paid:
          fighter.paid
            ? "PAID"
            : "UNPAID",

        Approved:
          fighter.approved
            ? "YES"
            : "NO",

        "Weighed In":
          fighter.weighed_in
            ? "YES"
            : "NO",
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(
        rows
      );

    // Useful column widths
    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 28 },
      { wch: 12 },
      { wch: 15 },
      { wch: 18 },
      { wch: 12 },
      { wch: 14 },
      { wch: 15 },
      { wch: 28 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Tournament Fighters"
    );

    const tournamentName =
      tournament?.name ||
      `Tournament_${tournamentId}`;

    // Make filename safe
    const safeName =
      String(tournamentName)
        .replace(
          /[^a-zA-Z0-9\u0400-\u04FF]+/g,
          "_"
        )
        .replace(
          /^_+|_+$/g,
          ""
        );

    XLSX.writeFile(
      workbook,
      `${safeName}_fighters.xlsx`
    );
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1115] text-gray-400 flex items-center justify-center">
        <div className="text-center">
          <div className="text-yellow-500 text-sm font-black uppercase tracking-[0.25em] mb-2">
            WARFC
          </div>

          <div className="text-sm">
            Loading tournament fighters...
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1115] text-red-400 p-6">
        <div className="max-w-3xl mx-auto">
          {error}
        </div>
      </div>
    );
  }

  // ==========================================================
  // TOURNAMENT DATE
  // ==========================================================

  const tournamentDate =
    formatTournamentDate(
      tournament?.startdate ||
        tournament?.start_date,
      tournament?.enddate ||
        tournament?.end_date
    );

  // ==========================================================
  // PAGE
  // ==========================================================

  return (
    <div className="min-h-screen bg-[#0f1115] text-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* ====================================================
            HEADER
        ===================================================== */}

        <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-[#171b24] via-[#11141b] to-black p-6 md:p-8 mb-6">

          {/* Background glow */}

          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-yellow-500/5 blur-3xl pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-6">

            <div>

              <div className="flex items-center gap-2 mb-3">

                <span className="w-2 h-2 rounded-full bg-yellow-500" />

                <span className="text-[10px] md:text-xs font-black tracking-[0.25em] uppercase text-yellow-500">
                  WARFC • OFFICIAL PARTICIPANTS
                </span>

              </div>

              <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none">
                Tournament Fighters
              </h1>

              <h2 className="mt-3 text-lg md:text-2xl font-bold text-gray-300">
                {tournament?.name ||
                  `Tournament #${tournamentId}`}
              </h2>

              <div className="flex flex-wrap items-center gap-3 mt-4">

                {tournamentDate && (
                  <span className="text-sm font-black text-yellow-500 uppercase tracking-wide">
                    {tournamentDate}
                  </span>
                )}

              </div>

            </div>

            {/* =================================================
                ADMIN ONLY
            ================================================== */}

            {isAdmin && (
              <button
                onClick={
                  exportToExcel
                }
                disabled={
                  !fighters.length
                }
                className="
                  shrink-0
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  px-5
                  py-3
                  rounded-xl
                  bg-yellow-500
                  text-black
                  text-xs
                  font-black
                  uppercase
                  tracking-wider
                  hover:bg-yellow-400
                  active:scale-95
                  transition
                  disabled:opacity-40
                  disabled:cursor-not-allowed
                "
              >
                <span className="text-base">
                  ↓
                </span>

                Export Excel
              </button>
            )}

          </div>
        </div>

        {/* ====================================================
            SUMMARY
        ===================================================== */}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">

          <div className="bg-[#151923] border border-gray-800 rounded-xl p-4">
            <div className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
              Total Fighters
            </div>

            <div className="text-2xl font-black text-white mt-1">
              {fighters.length}
            </div>
          </div>

          <div className="bg-[#151923] border border-gray-800 rounded-xl p-4">
            <div className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
              Male
            </div>

            <div className="text-2xl font-black text-cyan-400 mt-1">
              {maleCount}
            </div>
          </div>

          <div className="bg-[#151923] border border-gray-800 rounded-xl p-4">
            <div className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
              Female
            </div>

            <div className="text-2xl font-black text-pink-400 mt-1">
              {femaleCount}
            </div>
          </div>

          <div className="bg-[#151923] border border-gray-800 rounded-xl p-4">
            <div className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
              Clubs
            </div>

            <div className="text-2xl font-black text-yellow-400 mt-1">
              {clubCount}
            </div>
          </div>

        </div>

        {/* ====================================================
            CATEGORY
        ===================================================== */}

        <div className="bg-[#151923] border border-gray-800 rounded-xl p-2 mb-4">

          <div className="grid grid-cols-2 gap-2">

            <button
              onClick={() =>
                changeCategory(
                  "main"
                )
              }
              className={`py-3 rounded-lg font-black text-sm transition ${
                category === "main"
                  ? "bg-cyan-500 text-black"
                  : "bg-[#10131a] text-gray-400 hover:text-white"
              }`}
            >
              ҮНДСЭН

              <span className="ml-2 opacity-70">
                {mainCount}
              </span>
            </button>

            <button
              onClick={() =>
                changeCategory(
                  "altan_garaa"
                )
              }
              className={`py-3 rounded-lg font-black text-sm transition ${
                category ===
                "altan_garaa"
                  ? "bg-yellow-500 text-black"
                  : "bg-[#10131a] text-gray-400 hover:text-white"
              }`}
            >
              АЛТАН ГАРАА

              <span className="ml-2 opacity-70">
                {altanCount}
              </span>
            </button>

          </div>

        </div>

        {/* ====================================================
            FILTERS
        ===================================================== */}

        <div className="bg-[#151923] border border-gray-800 rounded-xl p-4 mb-6">

          {/* GENDER */}

          <div className="mb-4">

            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-bold">
              Gender
            </label>

            <div className="grid grid-cols-2 gap-2">

              <button
                onClick={() =>
                  changeGender(
                    "male"
                  )
                }
                className={`py-2.5 rounded-lg text-sm font-bold ${
                  gender === "male"
                    ? "bg-cyan-500 text-black"
                    : "bg-[#10131a] text-gray-400 border border-gray-800"
                }`}
              >
                Male

                <span className="ml-2 opacity-70">
                  {
                    categoryFighters.filter(
                      (f) =>
                        f.gender ===
                        "male"
                    ).length
                  }
                </span>
              </button>

              <button
                onClick={() =>
                  changeGender(
                    "female"
                  )
                }
                className={`py-2.5 rounded-lg text-sm font-bold ${
                  gender ===
                  "female"
                    ? "bg-pink-500 text-black"
                    : "bg-[#10131a] text-gray-400 border border-gray-800"
                }`}
              >
                Female

                <span className="ml-2 opacity-70">
                  {
                    categoryFighters.filter(
                      (f) =>
                        f.gender ===
                        "female"
                    ).length
                  }
                </span>
              </button>

            </div>
          </div>

        {/* AGE + WEIGHT BUTTONS */}
<div className="space-y-5">

  {/* AGE DIVISIONS */}
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-bold">
      Age Division
    </label>

    {!availableAges.length ? (
      <div className="text-sm text-gray-500">
        No registered age divisions
      </div>
    ) : (
      <div className="flex flex-wrap gap-2">
        {availableAges.map((age) => {
          const active = selectedAge === age;

          return (
            <button
              key={age}
              type="button"
              onClick={() => {
                setSelectedAge(age);
                setSelectedWeight("");
              }}
              className={`
                px-4 py-2.5 rounded-lg text-sm font-bold
                border transition-all duration-200
                ${
                  active
                    ? "bg-cyan-500 text-black border-cyan-400 shadow-lg shadow-cyan-500/20"
                    : "bg-[#10131a] text-gray-400 border-gray-700 hover:border-cyan-500 hover:text-white"
                }
              `}
            >
              {age}
            </button>
          );
        })}
      </div>
    )}
  </div>

  {/* WEIGHT CLASSES */}
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-bold">
      Weight Class
    </label>

    {!selectedAge ? (
      <div className="text-sm text-gray-500">
        Select an age division first
      </div>
    ) : !availableWeights.length ? (
      <div className="text-sm text-gray-500">
        No registered weight classes
      </div>
    ) : (
      <div className="flex flex-wrap gap-2">
        {availableWeights.map((weight) => {
          const active = selectedWeight === weight.name;

          return (
            <button
              key={weight.name}
              type="button"
              onClick={() =>
                setSelectedWeight(weight.name)
              }
              className={`
                min-w-[76px] px-4 py-2.5 rounded-lg
                text-sm font-bold border transition-all duration-200
                ${
                  active
                    ? "bg-cyan-500 text-black border-cyan-400 shadow-lg shadow-cyan-500/20"
                    : "bg-[#10131a] text-gray-400 border-gray-700 hover:border-cyan-500 hover:text-white"
                }
              `}
            >
              {weight.name}
            </button>
          );
        })}
      </div>
    )}
  </div>

</div>
</div>

        {/* ====================================================
            CURRENT SELECTION
        ===================================================== */}

        <div className="flex flex-wrap items-center gap-2 mb-4">

          <span
            className={`px-3 py-1 rounded-full text-xs font-bold ${
              category ===
              "altan_garaa"
                ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                : "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
            }`}
          >
            {category ===
            "altan_garaa"
              ? "АЛТАН ГАРАА"
              : "ҮНДСЭН"}
          </span>

          <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-800 text-gray-300">
            {gender === "male"
              ? "MALE"
              : "FEMALE"}
          </span>

          {selectedAge && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-800 text-gray-300">
              {selectedAge}
            </span>
          )}

          {selectedWeight && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-800 text-gray-300">
              {selectedWeight}
            </span>
          )}

        </div>

        {/* ====================================================
            FIGHTERS
        ===================================================== */}

        <div className="bg-[#151923] border border-gray-800 rounded-xl overflow-hidden">

          <div className="px-4 py-4 border-b border-gray-800 flex items-center justify-between">

            <div>

              <h2 className="font-black text-white uppercase tracking-tight">
                {selectedWeight ||
                  "Select Weight"}
              </h2>

              <p className="text-xs text-gray-500 mt-1">
                {selectedAge ||
                  "-"}{" "}
                •{" "}
                {gender ===
                "male"
                  ? "Male"
                  : "Female"}
              </p>

            </div>

            <div className="text-sm font-bold text-gray-400">
              {
                visibleFighters.length
              }{" "}
              fighters
            </div>

          </div>

          {!selectedAge ||
          !selectedWeight ? (
            <div className="p-10 text-center text-gray-500">
              Select an age division
              and weight class.
            </div>
          ) : !visibleFighters.length ? (
            <div className="p-10 text-center text-gray-500">
              No fighters registered
              in this category.
            </div>
          ) : (
            <div className="divide-y divide-gray-800">

              {visibleFighters.map(
                (
                  fighter,
                  index
                ) => (
                  <div
                    key={
                      fighter.registration_id ??
                      `${fighter.fighter_id}-${index}`
                    }
                    className="relative flex items-center gap-3 px-4 py-3 hover:bg-[#1a1f2a] transition"
                  >

                    {/* PAYMENT INDICATOR */}

                    <span
                      className={`absolute left-0 top-0 bottom-0 w-1 ${
                        fighter.paid
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />

                    {/* NUMBER */}

                    <div className="w-7 text-center text-xs text-gray-600 font-bold">
                      {index + 1}
                    </div>

                    {/* IMAGE */}

                    <img
                      src={
                        fighter.image ||
                        "/default-profile.png"
                      }
                      alt=""
                      onError={(
                        e
                      ) => {
                        e.currentTarget.src =
                          "/default-profile.png";
                      }}
                      className="w-10 h-10 rounded-full object-cover border border-gray-700"
                    />

                    {/* NAME */}

                    <div className="min-w-0 flex-1">

                      <div className="flex items-center gap-2 min-w-0">
                        <Link
                          to={`/fighters/${fighter.fighter_id}`}
                          className="min-w-0 truncate text-sm font-bold text-white hover:text-yellow-400"
                        >
                          {fighter.name}
                        </Link>

                        {fighter.actual_weight !== null &&
                          Number.isFinite(Number(fighter.actual_weight)) && (
                            <span
                              title="Actual weight"
                              className="
                                shrink-0
                                inline-flex
                                items-center
                                rounded-md
                                border border-yellow-500/25
                                bg-yellow-500/10
                                px-2 py-0.5
                                text-[10px]
                                font-black
                                tabular-nums
                                text-yellow-300
                              "
                            >
                              {Number(fighter.actual_weight).toFixed(1)}kg
                            </span>
                          )}
                      </div>

                      <div className="flex items-center gap-2 mt-1">

                        {fighter.club_logo && (
                          <img
                            src={
                              fighter.club_logo
                            }
                            alt=""
                            onError={(
                              e
                            ) => {
                              e.currentTarget.style.display =
                                "none";
                            }}
                            className="w-4 h-4 object-contain"
                          />
                        )}

                        {fighter.club_id ? (
                          <Link
                            to={`/clubs/${fighter.club_id}`}
                            className="text-xs text-gray-500 truncate hover:text-yellow-400 transition"
                          >
                            {
                              fighter.club
                            }
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-500 truncate">
                            {
                              fighter.club
                            }
                          </span>
                        )}

                      </div>

                    </div>

                    {/* WEIGHT */}

                    <div className="hidden sm:block text-right mr-4">

                      <div className="hidden sm:block text-right mr-4">
  <div className="text-sm font-bold text-gray-200">
    {fighter.weightclass_name || "-"}
  </div>
</div>

                    </div>

                    {/* PAYMENT */}

                    <div>

                      <span
                        className={`px-2 py-1 rounded text-[10px] font-bold ${
                          fighter.paid
                            ? "bg-green-500/15 text-green-400"
                            : "bg-red-500/15 text-red-400"
                        }`}
                      >
                        {fighter.paid
                          ? "ТӨЛСӨН"
                          : "ТӨЛӨӨГҮЙ"}
                      </span>

                    </div>
{/* ADMIN — EDIT CATEGORY */}
{isAdmin && (
  <Link
    to={`/tournaments/${tournamentId}/register?fighter=${fighter.fighter_id}&adminEdit=1`}
    className="
      shrink-0
      px-3 py-2
      rounded-lg
      border border-yellow-500/40
      bg-yellow-500/10
      text-yellow-400
      hover:bg-yellow-500
      hover:text-black
      text-[9px]
      font-black
      uppercase
      tracking-wider
      transition
    "
  >
    EDIT CATEGORY
  </Link>
)}
                  </div>
                )
              )}

            </div>
          )}

        </div>

{/* ====================================================
    REGISTERED CLUBS
===================================================== */}

<div className="bg-[#151923] border border-gray-800 rounded-xl p-4 mb-6">

  {/* HEADER */}

  <div className="flex items-center justify-between mb-4">

    <div>
      <h2 className="text-sm font-black uppercase tracking-wide text-white">
        Registered Clubs
      </h2>

      <p className="text-xs text-gray-500 mt-1">
        {clubsSummary.length} clubs • {fighters.length} fighters
      </p>
    </div>

  </div>

  {/* CLUB LOGOS */}

  <div className="flex flex-wrap gap-3">

    {clubsSummary.map((club) => (

      <div
        key={club.name}
        title={`${club.name} — ${club.count} fighters`}
        className="
          relative
          w-14
          h-14
          rounded-xl
          bg-[#10131a]
          border border-gray-800
          flex
          items-center
          justify-center
          hover:border-cyan-500/50
          hover:scale-105
          transition
          shrink-0
        "
      >

        {/* CLUB LOGO */}

        <div className="
          w-11
          h-11
          rounded-full
          bg-[#1a1f2a]
          overflow-hidden
          flex
          items-center
          justify-center
        ">

          {club.logo ? (

            <img
              src={resolveImage(
                club.logo,
                "club"
              )}
              alt={club.name}
              className="
                w-full
                h-full
                object-contain
                p-1
              "
              onError={(e) => {
                e.currentTarget.style.display =
                  "none";
              }}
            />

          ) : (

            <span className="text-sm font-black text-gray-500">
              {club.name.charAt(0)}
            </span>

          )}

        </div>

        {/* FIGHTER COUNT — ADMIN ONLY */}

{isAdmin && (
  <div
    className="
      absolute
      -right-1
      -bottom-1
      min-w-[22px]
      h-[22px]
      px-1
      rounded-full
      bg-cyan-500
      border-2
      border-[#151923]
      text-[#071018]
      text-[10px]
      font-black
      flex
      items-center
      justify-center
      shadow-lg
    "
  >
    {club.count}
  </div>
)}
      </div>

    ))}

  </div>

</div>
      </div>
    </div>
  );
}